import React, { useState, useEffect } from 'react';
import { getCandidateDetail, translateCandidateAbout } from '../services/api';
import './CandidateProfileModal.css';

/** Globe + formatted text block: translation + readable layout (used in About toolbar). */
function AboutTranslateFormatIcon() {
  return (
    <svg
      className="about-translate-icon-svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="9" cy="12" r="5.25" stroke="currentColor" strokeWidth="1.85" />
      <path d="M3.6 12h10.8" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" />
      <path
        d="M9 6.7c2.35 1.95 2.35 8.65 0 10.6"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
      />
      <path
        d="M9 6.7c-2.35 1.95-2.35 8.65 0 10.6"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
      />
      <rect x="14.35" y="11.85" width="8.65" height="9.9" rx="1.85" stroke="currentColor" strokeWidth="1.85" />
      <path d="M16.15 14.35h5.05M16.15 16.45h5.05M16.15 18.55h3.85" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function CandidateProfileModal({ candidate, candidateId, match, job, onClose }) {
  const effectiveCandidate = match?.candidate || candidate;
  const [candidateData, setCandidateData] = useState(effectiveCandidate);
  const [loading, setLoading] = useState(!effectiveCandidate && !!candidateId);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [expandedExperiences, setExpandedExperiences] = useState({});
  const [showInsights, setShowInsights] = useState(false);
  const [aboutViewMode, setAboutViewMode] = useState('original');
  const [translatedAboutText, setTranslatedAboutText] = useState(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateError, setTranslateError] = useState(null);

  // Match data
  const { insights, experience_match, skills_alignment, cultural_fit, pearch_score, job_requirements } = match || {};

  const getMatchFactorIcon = (percentage) => {
    if (percentage >= 90) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#10b981" />
        </svg>
      );
    } else if (percentage >= 70) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#f59e0b" />
          <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" />
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 22h20L12 2z" fill="#ef4444" />
        </svg>
      );
    }
  };

  useEffect(() => {
    if (!effectiveCandidate && candidateId) {
      // Fetch candidate detail if only ID provided
      const fetchCandidate = async () => {
        try {
          setLoading(true);
          const data = await getCandidateDetail(candidateId);
          setCandidateData(data);
        } catch (error) {
          console.error('Failed to load candidate:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchCandidate();
    } else if (effectiveCandidate) {
      setCandidateData(effectiveCandidate);
    }
  }, [effectiveCandidate, candidateId]);

  useEffect(() => {
    setAboutViewMode('original');
    setTranslatedAboutText(null);
    setTranslateError(null);
    setTranslateLoading(false);
  }, [candidateData?.id, candidateId, match?.id, match?.candidate_id, match?.pearch_docid]);

  if (loading) {
    return (
      <div className="modal-overlay-profile" onClick={onClose}>
        <div className="modal-container-profile" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">Loading candidate...</div>
        </div>
      </div>
    );
  }

  if (!candidateData) {
    return null;
  }

  const profile = candidateData?.pearch_profile || candidateData?.profile || candidateData || {};
  const candidateProfile = candidateData?.profile || candidateData || {};

  // Get practice areas from profile or pearch_profile
  const practiceAreas = (candidateProfile.practice_areas && Array.isArray(candidateProfile.practice_areas))
    ? candidateProfile.practice_areas
    : (profile.expertise && Array.isArray(profile.expertise) ? profile.expertise : []);

  // Get key skills from profile or pearch_profile
  const keySkills = (candidateProfile.skills && Array.isArray(candidateProfile.skills))
    ? candidateProfile.skills
    : (profile.expertise && Array.isArray(profile.expertise) ? profile.expertise : []);

  // Extract seniority from title
  const getSeniority = () => {
    const title = profile.title || '';
    if (title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes('partner')) return 'Partner';
      if (titleLower.includes('senior') || titleLower.includes('sr')) return 'Senior';
      if (titleLower.includes('counsel')) return 'Counsel';
      if (titleLower.includes('associate')) return 'Associate';
      if (titleLower.includes('mid') || titleLower.includes('mid-level')) return 'Mid-Level';
      return title;
    }
    return 'Not Specified';
  };

  const seniority = getSeniority();

  // Get email (prefer personal, fallback to business)
  const email = profile.best_personal_email || profile.best_business_email || '';
  const businessEmail = profile.best_business_email && profile.best_personal_email !== profile.best_business_email
    ? profile.best_business_email
    : null;

  // Get phone numbers
  const phoneNumbers = profile.phone_numbers || [];
  const primaryPhone = phoneNumbers.length > 0 ? phoneNumbers[0] : null;

  // Get LinkedIn URL
  const linkedinUrl = profile.linkedin_url || '';

  // Get experience summary (raw + placeholder when empty)
  const aboutSourceRaw = (profile.summary || candidateProfile.experience_summary || '').trim();
  const hasAboutText = aboutSourceRaw.length > 0;
  const experienceSummary = hasAboutText ? aboutSourceRaw : 'No experience summary available.';

  // Get educations with better data handling  
  const educations = profile.educations || profile.raw_profile?.educations || [];

  // Get experiences with better data handling
  const experiences = profile.experiences || profile.raw_profile?.experiences || [];

  // Helper functions for toggles
  const toggleExperience = (key) => {
    setExpandedExperiences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Truncate text with character limit
  const truncateText = (text, limit) => {
    if (!text) return '';
    if (text.length <= limit) return text;
    return text.substring(0, limit);
  };

  const dbCandidateId = candidateData?.id ?? match?.candidate_id ?? null;

  const clientTranslationContext = {
    full_name: profile.full_name,
    title: profile.title,
    location: profile.location,
    expertise: profile.expertise,
    total_experience_years: profile.total_experience_years,
    practice_areas: practiceAreas,
  };

  const handleAboutTranslateOrShowEnglish = async () => {
    if (!hasAboutText) return;
    if (translatedAboutText) {
      setAboutViewMode('translated');
      return;
    }
    setTranslateLoading(true);
    setTranslateError(null);
    try {
      const data = await translateCandidateAbout({
        text: aboutSourceRaw,
        candidateId: dbCandidateId,
        context: clientTranslationContext,
      });
      setTranslatedAboutText(data.translated_text || '');
      setAboutViewMode('translated');
    } catch (e) {
      setTranslateError('Translation failed. Please try again.');
    } finally {
      setTranslateLoading(false);
    }
  };

  const handleAboutTranslateIconClick = async () => {
    if (aboutViewMode === 'translated') {
      setAboutViewMode('original');
      return;
    }
    await handleAboutTranslateOrShowEnglish();
  };

  const ABOUT_CHAR_LIMIT = 600;
  const aboutText =
    aboutViewMode === 'translated' && translatedAboutText != null
      ? translatedAboutText
      : experienceSummary;
  const shouldShowAboutToggle = aboutText.length > ABOUT_CHAR_LIMIT;
  const displayedAboutText = isAboutExpanded ? aboutText : truncateText(aboutText, ABOUT_CHAR_LIMIT);

  return (
    <div className="modal-overlay-profile" onClick={onClose}>
      <div className="modal-container-profile" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="modal-close-btn-profile" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
          </svg>
        </button>

        {/* Modal Header */}
        <div className="modal-header-profile">
          <div className="candidate-header-info-profile">
            {profile.picture_url ? (
              <img
                src={profile.picture_url}
                alt={profile.full_name || 'Candidate'}
                className="candidate-modal-photo-profile"
              />
            ) : (
              <div className="candidate-modal-photo-placeholder-profile">
                {(profile.first_name?.[0] || profile.full_name?.[0] || '?').toUpperCase()}
              </div>
            )}
            <div className="candidate-header-text-profile">
              <h2 className="candidate-modal-name-profile">{profile.full_name || 'Unknown Candidate'}</h2>
              <div className="candidate-contact-info">
                {email && (
                  <a href={`mailto:${email}`} className="contact-item-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor" />
                    </svg>
                    <span>{email}</span>
                  </a>
                )}
                {primaryPhone && (
                  <a href={`tel:${primaryPhone}`} className="contact-item-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor" />
                    </svg>
                    <span>{primaryPhone}</span>
                  </a>
                )}
                {linkedinUrl && (
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-item-link linkedin"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 0H5C2.24 0 0 2.24 0 5V19C0 21.76 2.24 24 5 24H19C21.76 24 24 21.76 24 19V5C24 2.24 21.76 0 19 0ZM8 19H5V9H8V19ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5ZM20 19H17V13.5C17 12.12 15.88 11 14.5 11C13.12 11 12 12.12 12 13.5V19H9V9H12V10.5C12.8 9.5 14.04 9 15.5 9C18.54 9 21 11.46 21 14.5V19H20Z" fill="currentColor" />
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                )}
              </div>
            </div>
            {pearch_score !== undefined && (
              <div className="ai-match-score-badge">
                <div className="score-extra-large">{pearch_score * 20}</div>
                <div className="score-label-small">AI Match Score</div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Content */}
        <div className="modal-content-profile">
          {/* Location and Seniority Blocks */}
          <div className="info-blocks-row">
            <div className="info-block">
              <div className="info-block-icon location-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor" />
                </svg>
              </div>
              <div className="info-block-content">
                <div className="info-block-label">LOCATION</div>
                <div className="info-block-value">{profile.location || 'Not specified'}</div>
              </div>
            </div>

            <div className="info-block">
              <div className="info-block-icon seniority-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="currentColor" />
                </svg>
              </div>
              <div className="info-block-content">
                <div className="info-block-label">SENIORITY</div>
                <div className="info-block-value">{seniority}</div>
              </div>
            </div>
          </div>



          {/* About */}
          <div className="section-profile">
            <div className="about-section-head">
              <h3 className="section-title-profile about-section-title">
                <span className="about-section-title-inner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#8b5cf6" />
                  </svg>
                  About
                  {aboutViewMode === 'translated' && (
                    <span className="about-translated-badge" title="Showing English translation">
                      TRANSLATED
                    </span>
                  )}
                </span>
              </h3>
              {hasAboutText && (
                <div className="about-translate-toolbar">
                  {translateError && (
                    <span className="about-translate-error" role="status" aria-live="polite">
                      {translateError}
                    </span>
                  )}
                  <button
                    type="button"
                    className={`about-translate-icon-btn${aboutViewMode === 'translated' ? ' about-translate-icon-btn--active' : ''}`}
                    onClick={handleAboutTranslateIconClick}
                    disabled={translateLoading}
                    aria-pressed={aboutViewMode === 'translated'}
                    aria-busy={translateLoading}
                    aria-label={
                      aboutViewMode === 'translated'
                        ? 'Show original About text'
                        : 'Translate About to English (formatted)'
                    }
                    title={
                      aboutViewMode === 'translated'
                        ? 'Show original language'
                        : 'Translate to English and format for readability'
                    }
                  >
                    {translateLoading ? (
                      <span className="about-translate-spinner" aria-hidden="true" />
                    ) : (
                      <AboutTranslateFormatIcon />
                    )}
                  </button>
                </div>
              )}
            </div>
            <div className="about-text-container">
              <p className="about-text">{displayedAboutText}{!isAboutExpanded && shouldShowAboutToggle && '...'}</p>
              {shouldShowAboutToggle && (
                <button
                  className="toggle-btn"
                  onClick={() => setIsAboutExpanded(!isAboutExpanded)}
                >
                  {isAboutExpanded ? (
                    <>
                      <span>Show Less</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M7.41 15.41L12 10.83L16.59 15.41L18 14L12 8L6 14L7.41 15.41Z" fill="currentColor" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>Show More</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="currentColor" />
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Work Experience */}
          {experiences && experiences.length > 0 ? (
            <div className="section-profile">
              <h3 className="section-title-profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="#8b5cf6" />
                </svg>
                Work Experience
              </h3>
              <div className="experience-list">
                {experiences.map((experience, expIdx) => {
                  // Pearch API structure: experiences contain company_info and company_roles
                  const companyInfo = experience.company_info || {};
                  const companyRoles = experience.company_roles || [];

                  // If there are company_roles, map through them
                  if (companyRoles.length > 0) {
                    return companyRoles.map((role, roleIdx) => {
                      const experienceKey = `${expIdx}-${roleIdx}`;
                      const isExpanded = expandedExperiences[experienceKey];

                      return (
                        <div key={experienceKey} className="experience-item">
                          <div className="experience-header">
                            <div className="experience-title-group">
                              <h4 className="experience-title">{role.title || 'Position'}</h4>
                              <div className="experience-company-row">
                                <span className="experience-company">
                                  {role.company || companyInfo.name || 'Company'}
                                </span>
                                {companyInfo.linkedin_url && (
                                  <a
                                    href={companyInfo.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="experience-company-linkedin"
                                    title="View company on LinkedIn"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                      <path d="M19 0H5C2.24 0 0 2.24 0 5V19C0 21.76 2.24 24 5 24H19C21.76 24 24 21.76 24 19V5C24 2.24 21.76 0 19 0ZM8 19H5V9H8V19ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5ZM20 19H17V13.5C17 12.12 15.88 11 14.5 11C13.12 11 12 12.12 12 13.5V19H9V9H12V10.5C12.8 9.5 14.04 9 15.5 9C18.54 9 21 11.46 21 14.5V19H20Z" fill="currentColor" />
                                    </svg>
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="experience-duration">
                              {role.start_date && role.end_date
                                ? `${role.start_date} - ${role.end_date}`
                                : role.start_date
                                  ? `${role.start_date} - ${role.is_current_experience ? 'Present' : 'Present'}`
                                  : role.end_date
                                    ? `Until ${role.end_date}`
                                    : role.duration_years
                                      ? `${role.duration_years} years`
                                      : 'Duration not specified'
                              }
                            </div>
                          </div>
                          {role.location && (
                            <div className="experience-location">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor" />
                              </svg>
                              {role.location}
                            </div>
                          )}
                          {role.experience_summary && (
                            <>
                              {isExpanded && (
                                <p className="experience-description">{role.experience_summary}</p>
                              )}
                              <button
                                className="toggle-btn-small"
                                onClick={() => toggleExperience(experienceKey)}
                              >
                                {isExpanded ? (
                                  <>
                                    <span>Hide Details</span>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <path d="M7.41 15.41L12 10.83L16.59 15.41L18 14L12 8L6 14L7.41 15.41Z" fill="currentColor" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    <span>Show Details</span>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="currentColor" />
                                    </svg>
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      );
                    });
                  }

                  // Fallback: if no company_roles, try to display the experience itself
                  return (
                    <div key={expIdx} className="experience-item">
                      <div className="experience-header">
                        <div className="experience-title-group">
                          <h4 className="experience-title">{experience.title || 'Position'}</h4>
                          <span className="experience-company">
                            {experience.company || companyInfo.name || 'Company'}
                          </span>
                        </div>
                        <div className="experience-duration">Duration not specified</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="section-profile">
              <h3 className="section-title-profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="#8b5cf6" />
                </svg>
                Work Experience
              </h3>
              <div className="no-data-message">
                <p>No work experience data available for this candidate.</p>
              </div>
            </div>
          )}

          {/* Education */}
          {educations && educations.length > 0 ? (
            <div className="section-profile">
              <h3 className="section-title-profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" fill="#8b5cf6" />
                </svg>
                Education
              </h3>
              <div className="education-list">
                {educations.map((education, idx) => {
                  // Handle degree as array or string
                  const degreeDisplay = Array.isArray(education.degree)
                    ? education.degree.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')
                    : (education.degree || education.qualification || 'Degree');

                  const schoolName = education.school || education.institution || education.university || education.campus || 'School';

                  return (
                    <div key={idx} className="education-item">
                      <div className="education-header">
                        <div className="education-title-group">
                          <h4 className="education-degree">{degreeDisplay}</h4>
                          <span className="education-school">
                            {schoolName}
                          </span>
                        </div>
                        <div className="education-duration">
                          {education.start_date && education.end_date
                            ? `${education.start_date} - ${education.end_date}`
                            : education.start_date
                              ? `${education.start_date} - Present`
                              : education.end_date
                                ? `Graduated: ${education.end_date}`
                                : education.year
                                  ? `${education.year}`
                                  : 'Duration not specified'
                          }
                        </div>
                      </div>

                      {education.location && (
                        <div className="education-location">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor" />
                          </svg>
                          {education.location}
                        </div>
                      )}

                      {/* Display all additional fields */}
                      <div className="education-all-details">
                        {/* Campus field removed as it is now likely the School Name or redundant */}

                        {education.major && (
                          <p className="education-field">
                            <strong>Major:</strong> {education.major}
                          </p>
                        )}

                        {education.field_of_study && (
                          <p className="education-field">
                            <strong>Field of Study:</strong> {education.field_of_study}
                          </p>
                        )}

                        {education.specialization && (
                          <p className="education-field">
                            <strong>Specialization:</strong> {education.specialization}
                          </p>
                        )}

                        {education.specialization_category && (
                          <p className="education-field">
                            <strong>Category:</strong> {education.specialization_category}
                          </p>
                        )}

                        {education.gpa && (
                          <p className="education-gpa">
                            <strong>GPA:</strong> {education.gpa}
                          </p>
                        )}

                        {education.university_linkedin_url && (
                          <p className="education-link-container">
                            <a
                              href={education.university_linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="education-linkedin-link"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M19 0H5C2.24 0 0 2.24 0 5V19C0 21.76 2.24 24 5 24H19C21.76 24 24 21.76 24 19V5C24 2.24 21.76 0 19 0ZM8 19H5V9H8V19ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5ZM20 19H17V13.5C17 12.12 15.88 11 14.5 11C13.12 11 12 12.12 12 13.5V19H9V9H12V10.5C12.8 9.5 14.04 9 15.5 9C18.54 9 21 11.46 21 14.5V19H20Z" fill="currentColor" />
                              </svg>
                              View University Profile
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="section-profile">
              <h3 className="section-title-profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" fill="#8b5cf6" />
                </svg>
                Education
              </h3>
              <div className="no-data-message">
                <p>No education data available for this candidate.</p>
              </div>
            </div>
          )}

          {/* Key Skills */}
          {keySkills.length > 0 && (
            <div className="section-profile">
              <h3 className="section-title-profile">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#8b5cf6" />
                </svg>
                Key Skills
              </h3>
              <div className="tags-container">
                {keySkills.map((skill, idx) => (
                  <span key={idx} className="skill-tag-profile">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Match Metrics Section - Only shown if match data exists */}
          {match && (
            <div className="match-metrics-container">
              {/* AI Match Analysis */}
              <div className="match-analysis-section">
                <h3 className="section-title-profile">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M13 3C9.23 3 6.19 5.95 6 9.66L4.08 12.19C3.84 12.5 4.08 13 4.5 13H6V16C6 17.11 6.89 18 8 18H9V21H15V16.31C17.37 15.19 19 12.8 19 10C19 6.13 15.87 3 12 3H13Z" fill="#8b5cf6" />
                  </svg>
                  AI Match Analysis
                </h3>
                <div className="analysis-content">
                  <p className="analysis-summary">
                    {insights?.overall_summary ||
                      `${effectiveCandidate?.full_name} demonstrates strong alignment with the position based on their extensive experience.`}
                  </p>
                  <p className="analysis-disclaimer">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px', flexShrink: 0, marginTop: '1px' }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor" />
                    </svg>
                    AI analysis is generated from enriched profile data and may reference details not
                    visible on the candidate's public LinkedIn. Always verify key claims directly with
                    the candidate.
                  </p>
                </div>
              </div>

              {/* Key Match Factors */}
              <div className="match-factors-section">
                <h3 className="section-title-profile">Key Match Factors</h3>
                <div className="factors-grid">
                  <div className="factor-item">
                    {getMatchFactorIcon(experience_match)}
                    <div className="factor-content">
                      <div className="factor-label">Experience Match</div>
                      <div className="factor-value">{experience_match}%</div>
                      <div className="factor-description">
                        {effectiveCandidate?.total_experience_years || 0} years of relevant experience
                      </div>
                    </div>
                  </div>

                  <div className="factor-item">
                    {getMatchFactorIcon(skills_alignment)}
                    <div className="factor-content">
                      <div className="factor-label">Skills Alignment</div>
                      <div className="factor-value">{skills_alignment}%</div>
                      <div className="factor-description">
                        Strong match in {effectiveCandidate?.expertise?.length || 0} key skills
                      </div>
                    </div>
                  </div>

                  <div className="factor-item">
                    {getMatchFactorIcon(cultural_fit)}
                    <div className="factor-content">
                      <div className="factor-label">Cultural Fit</div>
                      <div className="factor-value">{cultural_fit}%</div>
                      <div className="factor-description">
                        Aligned with company values and work style
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Insights Toggle */}
              <div className="match-insights-section">
                <button
                  className="toggle-insights-btn"
                  onClick={() => setShowInsights(!showInsights)}
                >
                  {showInsights ? 'Hide' : 'Show'} Detailed Insights & Requirements Analysis
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" transform={showInsights ? 'rotate(180)' : ''} style={{ transition: 'transform 0.2s' }}>
                    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {showInsights && (
                  <div style={{ marginTop: '24px', animation: 'fadeIn 0.3s ease-out' }}>
                    {/* Requirements vs Profile */}
                    <div className="match-comparison-section">
                      <h3 className="section-title-profile">Requirements vs Profile</h3>
                      <div className="comparison-table">
                        <div className="comparison-row comparison-header">
                          <div className="comparison-col">Job Requirements</div>
                          <div className="comparison-col">Candidate Profile</div>
                        </div>

                        {/* Experience */}
                        {(job_requirements?.experience_required || job_requirements?.qualifications) && (
                          <div className="comparison-row">
                            <div className="comparison-col">
                              <div className="requirement-label">Experience</div>
                              <div className="requirement-value">
                                {job_requirements.experience_required ||
                                  job_requirements.qualifications?.find(q => q.includes('year')) ||
                                  'Experience required'}
                              </div>
                            </div>
                            <div className="comparison-col">
                              <div className="profile-value">{effectiveCandidate?.total_experience_years || 0} years</div>
                              <span className={`match-badge ${(effectiveCandidate?.total_experience_years || 0) >= 3 ? 'success' : 'warning'}`}>
                                {(effectiveCandidate?.total_experience_years || 0) >= 3 ? '✓ Meets' : '⚠ Review'}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Skills */}
                        {(job_requirements?.skills_needed || job_requirements?.practice_area || effectiveCandidate?.expertise) && (
                          <div className="comparison-row">
                            <div className="comparison-col">
                              <div className="requirement-label">Skills</div>
                              <div className="requirement-value">
                                {job_requirements?.practice_area ||
                                  job_requirements?.skills_needed?.slice(0, 2).join(', ') ||
                                  (job && job.practice_area) ||
                                  'Skills required'}
                              </div>
                            </div>
                            <div className="comparison-col">
                              <div className="profile-value">{effectiveCandidate?.expertise?.slice(0, 3).join(', ') || 'Not specified'}</div>
                              <span className={`match-badge ${skills_alignment >= 80 ? 'success' : skills_alignment >= 60 ? 'warning' : 'danger'}`}>
                                {skills_alignment >= 80 ? '✓ Strong match' : skills_alignment >= 60 ? '⚠ Partial match' : '✗ Weak match'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Detailed AI Insights */}
                    {insights?.query_insights && insights.query_insights.length > 0 && (
                      <div className="detailed-insights">
                        <h3 className="section-title-profile">Detailed AI Findings</h3>
                        <div className="insights-list">
                          {insights.query_insights.map((insight, idx) => (
                            <div key={idx} className="insight-item">
                              <div className="insight-header">
                                <span className={`insight-priority ${insight.priority?.toLowerCase() || 'standard'}`}>
                                  {insight.priority || 'Standard'}
                                </span>
                                <span className="insight-match-level">{insight.match_level}</span>
                              </div>
                              <div className="insight-subquery">{insight.subquery}</div>
                              <div className="insight-rationale">{insight.rationale}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default CandidateProfileModal;
